import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Container,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import { formatDateFromAPI } from "../helpers/functions";

const statusBadge = (status) => {
  const map = {
    pending: "warning",
    passed: "success",
    failed: "danger",
    retrying: "info",
  };
  return (
    <Badge bg={map[status] || "secondary"}>{status}</Badge>
  );
};

export default function InboundMonitoring() {
  const navigate = useNavigate();
  const location = useLocation();

  const [gateways, setGateways] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState("");
  const [runs, setRuns] = useState([]);
  const [totalRuns, setTotalRuns] = useState(0);
  const [runsOffset, setRunsOffset] = useState(0);
  const RUNS_LIMIT = 20;

  const [selectedRun, setSelectedRun] = useState(null);
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [resultsOffset, setResultsOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const RESULTS_LIMIT = 50;

  const [triggering, setTriggering] = useState(false);

  // ---- numbers for selected gateway ----
  const [numbers, setNumbers] = useState([]);
  const [selectedNumberIds, setSelectedNumberIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState("all");
  const [selectionRules, setSelectionRules] = useState([]);
  const [remainderPercent, setRemainderPercent] = useState("");
  const [customFromNumber, setCustomFromNumber] = useState("");

  const supplierOptions = useMemo(() => {
    const grouped = new Map();
    numbers.forEach((n) => {
      const sid = Number(n.supplier_id || 0);
      if (!grouped.has(sid)) {
        grouped.set(sid, {
          supplier_id: sid,
          supplier_name: n.supplier_name || `Supplier ${sid}`,
          count: 0,
        });
      }
      grouped.get(sid).count += 1;
    });
    return Array.from(grouped.values()).sort((a, b) => a.supplier_name.localeCompare(b.supplier_name));
  }, [numbers]);

  const selectionPreview = useMemo(() => {
    if (selectionMode === "all" || selectedNumberIds.length > 0 || numbers.length === 0) {
      return null;
    }

    const normalizedRules = selectionRules
      .map((r) => ({
        supplier_id: Number.parseInt(r.supplier_id, 10),
        value: Number.parseInt(r.value, 10),
      }))
      .filter((r) => Number.isFinite(r.supplier_id) && r.supplier_id >= 0 && Number.isFinite(r.value) && r.value > 0);

    const normalizedRemainderPercent = Number.parseInt(remainderPercent, 10);

    if (normalizedRules.length === 0) {
      return {
        totalNumbers: numbers.length,
        selectedTotal: 0,
        rulesTotal: 0,
        rows: [],
      };
    }

    const totalNumbers = numbers.length;
    const initialCounts = new Map();
    numbers.forEach((n) => {
      const sid = Number(n.supplier_id || 0);
      initialCounts.set(sid, (initialCounts.get(sid) || 0) + 1);
    });

    const remainingCounts = new Map(initialCounts);
    const baseBySupplier = new Map();
    const remainderBySupplier = new Map();
    const ruleSupplierIDs = new Set(normalizedRules.map((r) => r.supplier_id));

    normalizedRules.forEach((rule) => {
      const available = remainingCounts.get(rule.supplier_id) || 0;
      const requested = selectionMode === "percentage"
        ? Math.ceil((available * rule.value) / 100)
        : rule.value;
      const take = Math.min(requested, available);
      remainingCounts.set(rule.supplier_id, available - take);
      baseBySupplier.set(rule.supplier_id, (baseBySupplier.get(rule.supplier_id) || 0) + take);
    });

    if (Number.isFinite(normalizedRemainderPercent) && normalizedRemainderPercent > 0) {
      remainingCounts.forEach((available, sid) => {
        if (ruleSupplierIDs.has(sid) || available <= 0) return;
        const take = Math.min(Math.ceil((available * normalizedRemainderPercent) / 100), available);
        if (take > 0) {
          remainderBySupplier.set(sid, (remainderBySupplier.get(sid) || 0) + take);
          remainingCounts.set(sid, available - take);
        }
      });
    }

    const supplierMeta = new Map(supplierOptions.map((s) => [s.supplier_id, s]));
    const allSupplierIDs = new Set([
      ...Array.from(initialCounts.keys()),
      ...Array.from(baseBySupplier.keys()),
      ...Array.from(remainderBySupplier.keys()),
    ]);

    const rows = Array.from(allSupplierIDs)
      .map((sid) => {
        const initial = initialCounts.get(sid) || 0;
        const base = baseBySupplier.get(sid) || 0;
        const remainder = remainderBySupplier.get(sid) || 0;
        const selected = base + remainder;
        const name = supplierMeta.get(sid)?.supplier_name || `Supplier ${sid}`;
        return {
          supplier_id: sid,
          supplier_name: name,
          initial,
          base,
          remainder,
          selected,
        };
      })
      .filter((r) => r.selected > 0 || r.initial > 0)
      .sort((a, b) => a.supplier_name.localeCompare(b.supplier_name));

    const selectedTotal = rows.reduce((sum, r) => sum + r.selected, 0);
    const rulesTotal = selectionMode === "percentage"
      ? normalizedRules.reduce((sum, r) => sum + r.value, 0)
      : normalizedRules.reduce((sum, r) => sum + r.value, 0);

    return {
      totalNumbers,
      selectedTotal,
      rulesTotal,
      rows,
    };
  }, [numbers, remainderPercent, selectionMode, selectionRules, selectedNumberIds.length, supplierOptions]);

  // ---- load gateway configs ----
  const fetchGateways = async () => {
    const resp = await fetch("/auth/gateway-configs");
    const data = await resp.json();
    if (resp.ok) {
      setGateways(data.data || []);
    } else {
      if (resp.status === 401) navigate("/login", { state: { from: location } });
    }
  };

  // ---- load numbers for selected gateway ----
  const fetchNumbers = async (gatewayId) => {
    if (!gatewayId) {
      setNumbers([]);
      setSelectedNumberIds([]);
      setSelectionMode("all");
      setSelectionRules([]);
      setRemainderPercent("");
      setCustomFromNumber("");
      return;
    }
    const resp = await fetch(
      `/auth/monitored-numbers?gateway_config_id=${gatewayId}&active=true&limit=200`
    );
    const data = await resp.json();
    if (resp.ok) {
      setNumbers(data.data || []);
      setSelectedNumberIds([]);
      setSelectionMode("all");
      setSelectionRules([]);
      setRemainderPercent("");
      setCustomFromNumber("");
    }
  };

  const addSelectionRule = () => {
    if (supplierOptions.length === 0) return;
    setSelectionRules((prev) => [
      ...prev,
      { supplier_id: supplierOptions[0].supplier_id, value: "" },
    ]);
  };

  const updateSelectionRule = (idx, patch) => {
    setSelectionRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeSelectionRule = (idx) => {
    setSelectionRules((prev) => prev.filter((_, i) => i !== idx));
  };

  // ---- load test runs ----
  const fetchRuns = async () => {
    let url = `/auth/test-runs?limit=${RUNS_LIMIT}&offset=${runsOffset}`;
    if (selectedGateway) url += `&gateway_config_id=${selectedGateway}`;
    const resp = await fetch(url);
    const data = await resp.json();
    if (resp.ok) {
      setRuns(data.data || []);
      setTotalRuns(data.total || 0);
    } else {
      toast.error(data.error || "Failed to load test runs");
      if (resp.status === 401) navigate("/login", { state: { from: location } });
    }
  };

  // ---- load test results for a run ----
  const fetchResults = async (runId, offset = 0) => {
    let url = `/auth/test-results?run_id=${runId}&limit=${RESULTS_LIMIT}&offset=${offset}`;
    if (statusFilter) url += `&status=${statusFilter}`;
    const resp = await fetch(url);
    const data = await resp.json();
    if (resp.ok) {
      setResults(data.data || []);
      setTotalResults(data.total || 0);
    } else {
      toast.error(data.error || "Failed to load results");
    }
  };

  useEffect(() => {
    fetchGateways();
  }, []);

  useEffect(() => {
    fetchRuns();
    fetchNumbers(selectedGateway);
  }, [selectedGateway, runsOffset]);

  useEffect(() => {
    if (selectedRun) {
      fetchResults(selectedRun.id, resultsOffset);
    }
  }, [selectedRun, resultsOffset, statusFilter]);

  const triggerRun = async () => {
    if (triggering) return;
    setTriggering(true);
    const body = {};
    if (selectedGateway) body.gateway_config_id = parseInt(selectedGateway);
    if (customFromNumber.trim()) body.custom_from = customFromNumber.trim();
    if (selectedNumberIds.length > 0) {
      body.number_ids = selectedNumberIds;
    } else if (selectionMode !== "all") {
      if (!selectedGateway) {
        setTriggering(false);
        toast.error("Please select a gateway before using random supplier selection");
        return;
      }
      const rules = selectionRules
        .map((r) => ({
          supplier_id: parseInt(r.supplier_id),
          value: parseInt(r.value),
        }))
        .filter((r) => Number.isFinite(r.supplier_id) && r.supplier_id >= 0 && Number.isFinite(r.value) && r.value > 0);

      if (rules.length === 0) {
        setTriggering(false);
        toast.error("Please add at least one supplier rule with a positive value");
        return;
      }

    if (selectionMode === "percentage") {
    const totalPct = rules.reduce((sum, r) => sum + r.value, 0);
    if (totalPct > 100) {
      setTriggering(false);
      toast.error(`Total percentage cannot exceed 100% (currently ${totalPct}%)`);
      return;
    }
    }

      const remainderPct = Number.parseInt(remainderPercent, 10);
      if (Number.isFinite(remainderPct) && remainderPct > 100) {
        setTriggering(false);
        toast.error(`Remainder percentage cannot exceed 100% (currently ${remainderPct}%)`);
        return;
      }

      body.number_selection = {
        mode: selectionMode,
        rules,
      };

      if (Number.isFinite(remainderPct) && remainderPct > 0) {
        body.number_selection.remainder_percentage = remainderPct;
      }
    }
    const resp = await fetch("/auth/test-runs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    setTriggering(false);
    if (resp.ok) {
      toast.success(data.message);
      setTimeout(fetchRuns, 1000);
    } else {
      toast.error(data.error || "Failed to trigger run");
    }
  };

  const selectRun = (run) => {
    setSelectedRun(run);
    setResultsOffset(0);
    setStatusFilter("");
    setResults([]);
  };

  const retryRun = async (run, e) => {
    e.stopPropagation();
    const resp = await fetch(`/auth/test-runs/${run.id}/retry`, { method: "POST" });
    const data = await resp.json();
    if (resp.ok) {
      toast.success(`Retry triggered for ${data.retrying} number(s)`);
      setTimeout(fetchRuns, 1000);
    } else {
      toast.error(data.error || "Failed to retry run");
    }
  };

  const passRate = (run) => {
    if (!run.total_sent) return "—";
    const pct = Math.round((run.total_passed / run.total_sent) * 100);
    return `${pct}%`;
  };

  return (
    <Container fluid>
      {/* Header + controls */}
      <div className="d-flex align-items-center my-3 gap-3 flex-wrap">
        <h3 className="mb-0">Inbound Monitoring</h3>

        <Form.Select
          style={{ width: 220 }}
          value={selectedGateway}
          onChange={(e) => {
            setSelectedGateway(e.target.value);
            setRunsOffset(0);
            setSelectedRun(null);
          }}
        >
          <option value="">All Gateways</option>
          {gateways.map((g) => (
            <option key={g.id} value={g.id}>
              {g.gateway_name}
            </option>
          ))}
        </Form.Select>

        {numbers.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Form.Label className="mb-0 small text-muted">
              Numbers ({selectedNumberIds.length === 0 ? "all" : selectedNumberIds.length + " selected"})
            </Form.Label>
            <Form.Select
              multiple
              size={Math.min(numbers.length, 5)}
              style={{ width: 220, minHeight: 36 }}
              value={selectedNumberIds.map(String)}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, (o) =>
                  parseInt(o.value)
                );
                setSelectedNumberIds(selected);
              }}
            >
              {numbers.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.number} ({n.number_type})
                </option>
              ))}
            </Form.Select>
            {selectedNumberIds.length > 0 && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-start"
                onClick={() => setSelectedNumberIds([])}
              >
                Clear selection
              </button>
            )}

            <div>
              <Form.Label className="mb-0 small text-muted">Random selection mode</Form.Label>
              <Form.Select
                style={{ width: 260, minHeight: 36 }}
                value={selectionMode}
                onChange={(e) => {
                  setSelectionMode(e.target.value);
                  if (e.target.value === "all") {
                    setSelectionRules([]);
                    setRemainderPercent("");
                  }
                }}
              >
                <option value="all">All active numbers</option>
                <option value="percentage">Random by supplier percentage</option>
                <option value="count">Random by supplier count</option>
              </Form.Select>
            </div>

            {selectionMode !== "all" && selectedNumberIds.length === 0 && (
              <div style={{ width: 330 }}>
                <Form.Label className="mb-1 small text-muted">
                  Supplier rules ({selectionMode === "percentage" ? "% of supplier" : "count from supplier"})
                </Form.Label>
                {selectionRules.map((rule, idx) => (
                  <div key={`${rule.supplier_id}-${idx}`} className="d-flex gap-2 mb-1 align-items-center">
                    <Form.Select
                      size="sm"
                      value={String(rule.supplier_id)}
                      onChange={(e) =>
                        updateSelectionRule(idx, { supplier_id: parseInt(e.target.value) })
                      }
                    >
                      {supplierOptions.map((s) => (
                        <option key={s.supplier_id} value={s.supplier_id}>
                          {s.supplier_name} ({s.count})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={1}
                      max={selectionMode === "percentage" ? 100 : undefined}
                      placeholder={selectionMode === "percentage" ? "%" : "count"}
                      value={rule.value}
                      onChange={(e) => updateSelectionRule(idx, { value: e.target.value })}
                      style={{ width: 90 }}
                    />
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => removeSelectionRule(idx)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}

                <Form.Label className="mb-1 small text-muted mt-2">
                  Remainder percentage for all remaining suppliers
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  max={100}
                  placeholder="e.g. 10"
                  value={remainderPercent}
                  onChange={(e) => setRemainderPercent(e.target.value)}
                  style={{ width: 120 }}
                />

                <div className="d-flex gap-2 align-items-center mt-2">
                  <Button size="sm" variant="outline-primary" onClick={addSelectionRule}>
                    + Rule
                  </Button>
                </div>

                {selectionPreview && (
                  <div className="small mt-2" style={{ border: "1px solid #dee2e6", borderRadius: 6, padding: 8 }}>
                    <div className="fw-semibold mb-1">Selection preview</div>
                    <div className="text-muted mb-1">
                      Selected {selectionPreview.selectedTotal} of {selectionPreview.totalNumbers}
                      {selectionMode === "percentage" && (
                        <span> | Rules total: {selectionPreview.rulesTotal}%</span>
                      )}
                    </div>
                    {selectionPreview.rows.map((r) => (
                      <div key={r.supplier_id}>
                        {r.supplier_name}: base {r.base}, remainder {r.remainder}, total {r.selected} / {r.initial}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectionMode !== "all" && selectedNumberIds.length > 0 && (
              <div className="small text-muted">
                Explicit number selection is active, so random supplier rules are ignored.
              </div>
            )}

            <div>
              <Form.Label className="mb-0 small text-muted">Custom from number (optional)</Form.Label>
              <Form.Control
                size="sm"
                style={{ width: 260 }}
                value={customFromNumber}
                onChange={(e) => setCustomFromNumber(e.target.value)}
                placeholder="e.g. 447700900123 or sender id"
              />
            </div>
          </div>
        )}

        <Button
          variant="primary"
          size="sm"
          onClick={triggerRun}
          disabled={triggering}
        >
          {triggering ? "Triggering…" : "▶ Trigger Run"}
        </Button>
      </div>

      <Row>
        {/* Left: test runs list */}
        <Col md={selectedRun ? 5 : 12}>
          <h5>Test Runs</h5>
          <Table bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Batch</th>
                <th>Gateway</th>
                <th>Triggered</th>
                <th>Started</th>
                <th>Sent</th>
                <th>Passed</th>
                <th>Failed</th>
                <th>Pass %</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr
                  key={run.id}
                  onClick={() => selectRun(run)}
                  style={{ cursor: "pointer" }}
                  className={selectedRun?.id === run.id ? "table-active" : ""}
                >
                  <td>
                    <code className="small">{run.batch_number?.slice(0, 8)}…</code>
                  </td>
                  <td>{run.gateway_config?.gateway_name || "—"}</td>
                  <td>
                    <Badge bg={run.triggered_by === "manual" ? "primary" : "secondary"}>
                      {run.triggered_by}
                    </Badge>
                  </td>
                  <td className="text-nowrap small">
                    {run.started_at ? formatDateFromAPI(run.started_at) : "—"}
                  </td>
                  <td>{run.total_sent}</td>
                  <td>
                    <span className="text-success">{run.total_passed}</span>
                  </td>
                  <td>
                    <span
                      className={run.total_failed > 0 ? "text-danger" : "text-muted"}
                    >
                      {run.total_failed}
                    </span>
                  </td>
                  <td>{passRate(run)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {run.total_failed > 0 && (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={(e) => retryRun(run, e)}
                      >
                        ↺ Retry
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    No runs yet. Trigger one above or configure a cron schedule via{" "}
                    <code>VN_TEST_CRON</code>.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* Runs pagination */}
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={runsOffset === 0}
              onClick={() => setRunsOffset(Math.max(0, runsOffset - RUNS_LIMIT))}
            >
              ← Prev
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={runsOffset + RUNS_LIMIT >= totalRuns}
              onClick={() => setRunsOffset(runsOffset + RUNS_LIMIT)}
            >
              Next →
            </Button>
            <span className="text-muted small align-self-center">
              {totalRuns} total
            </span>
          </div>
        </Col>

        {/* Right: results for selected run */}
        {selectedRun && (
          <Col md={7}>
            <div className="d-flex align-items-center gap-3 mb-2">
              <h5 className="mb-0">
                Results{" "}
                <code className="small">{selectedRun.batch_number?.slice(0, 8)}…</code>
              </h5>
              <Form.Select
                style={{ width: 150 }}
                size="sm"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setResultsOffset(0);
                }}
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
                <option value="retrying">Retrying</option>
              </Form.Select>

              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => fetchResults(selectedRun.id, resultsOffset)}
              >
                ↻ Refresh
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setSelectedRun(null)}
              >
                ✕ Close
              </Button>
            </div>

            <Table bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Supplier</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Delivery</th>
                  <th>Sent To</th>
                  <th>Sent at</th>
                  <th>Received at</th>
                  <th>Latency</th>
                  <th>Retries</th>
                  <th>ACK Src</th>
                  <th>ACK Source IP</th>
                  <th>Referer</th>
                  <th>User-Agent</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <code>{r.number?.number || "—"}</code>
                    </td>
                    <td>
                      <span title={r.supplier_name || r.number?.supplier_name || ""}>
                        {r.supplier_name || r.number?.supplier_name || "—"}
                      </span>
                    </td>
                    <td>
                      <Badge bg={r.number?.number_type === "vn" ? "primary" : "dark"}>
                        {r.number?.number_type || "—"}
                      </Badge>
                    </td>
                    <td>{statusBadge(r.status)}</td>
                    <td>
                      <Badge
                        bg={
                          r.delivery_status === "delivered"
                            ? "success"
                            : r.delivery_status === "failed"
                              ? "danger"
                              : r.delivery_status === "sent"
                                ? "info"
                                : "secondary"
                        }
                      >
                        {r.delivery_status || "pending"}
                      </Badge>
                    </td>
                    <td><code>{r.sent_to_number || "—"}</code></td>
                    <td className="text-nowrap small">
                      {r.triggered_at ? formatDateFromAPI(r.triggered_at) : "—"}
                    </td>
                    <td className="text-nowrap small">
                      {r.received_at?.Valid
                        ? formatDateFromAPI(r.received_at.Time)
                        : "—"}
                    </td>
                    <td>
                      {r.latency_ms > 0 ? `${r.latency_ms} ms` : "—"}
                    </td>
                    <td>{r.retry_count}</td>
                    <td>
                      {r.ack_gateway_src
                        ? <span className="badge bg-info text-dark">{r.ack_gateway_src}</span>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td className="text-nowrap small">
                      <code>{r.ack_source_ip || "—"}</code>
                    </td>
                    <td className="small" style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.ack_referer || ""}>
                      {r.ack_referer || "—"}
                    </td>
                    <td className="small" style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.ack_user_agent || ""}>
                      {r.ack_user_agent || "—"}
                    </td>
                  </tr>
                ))}
                {results.length === 0 && (
                  <tr>
                    <td colSpan={14} className="text-center text-muted">
                      No results
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {/* Results pagination */}
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={resultsOffset === 0}
                onClick={() =>
                  setResultsOffset(Math.max(0, resultsOffset - RESULTS_LIMIT))
                }
              >
                ← Prev
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={resultsOffset + RESULTS_LIMIT >= totalResults}
                onClick={() => setResultsOffset(resultsOffset + RESULTS_LIMIT)}
              >
                Next →
              </Button>
              <span className="text-muted small align-self-center">
                {totalResults} total
              </span>
            </div>
          </Col>
        )}
      </Row>
    </Container>
  );
}
